import { isBoolean, isNil } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDeepCompareMemo } from 'use-deep-compare';

import { type AssetSearchFilters, type SearchFilterDate, type SearchFilterFields } from '@air/api/types';

import { type ItemTypeFilter } from '~/components/Filters/types';
import { FilterParamNames } from '~/constants/search';
import { useFiltersContext } from '~/providers/FiltersProvider';
import { similarToImageFilterSelector } from '~/store/filters/selectors';
import { type SeparateLocationParam, type SeparateLocationsParam } from '~/utils/filters/types';
import { customFieldIdFromUrlParam } from '~/utils/filters/urlParamsToFiltersUtils';

export const useFilterParams = () => {
  const { filters: userDefinedFilters, immutableFilters, accountId } = useFiltersContext();

  // Read similarToImage from Redux store instead of URL to avoid large base64 strings in URL
  const similarToImageFromRedux = useSelector(similarToImageFilterSelector);

  const filters = useDeepCompareMemo(
    () => ({
      ...userDefinedFilters,
      ...immutableFilters,
    }),
    [userDefinedFilters, immutableFilters],
  );

  const boardParam = useMemo((): AssetSearchFilters['board'] | null => {
    const boardId = filters[FilterParamNames.boardId];
    if (boardId) {
      return { is: boardId };
    }
    return null;
  }, [filters]);

  const libraryParam = useMemo((): AssetSearchFilters['library'] | null => {
    const libraryId = filters[FilterParamNames.libraryId];
    if (libraryId) {
      return { is: libraryId };
    }
    return null;
  }, [filters]);

  const onlyInLibrariesParam = useMemo((): AssetSearchFilters['onlyInLibraries'] | null => {
    const onlyInLibraries = filters[FilterParamNames.onlyInLibraries];
    if (isBoolean(onlyInLibraries)) {
      return { is: onlyInLibraries };
    }
    return null;
  }, [filters]);

  const typesParam = useMemo((): ItemTypeFilter[] => {
    if (filters[FilterParamNames.type]?.length) {
      return filters[FilterParamNames.type] as ItemTypeFilter[];
    }
    return [];
  }, [filters]);

  const assetFileKindParam = useMemo((): AssetSearchFilters['assetFileKind'] | null => {
    const assetFileKind = filters[FilterParamNames.assetFileKind];

    if (!Array.isArray(assetFileKind)) {
      return null;
    }

    if (assetFileKind.length === 1) {
      return { is: assetFileKind[0] };
    }

    return {
      or: assetFileKind,
    };
  }, [filters]);

  const customFieldsParam = useMemo((): SearchFilterFields[] => {
    const customFields: SearchFilterFields[] = [];

    if (filters.customFields) {
      Object.keys(filters.customFields).forEach((paramName) => {
        const values = filters.customFields?.[paramName];
        if (!values) return;

        const cfId = customFieldIdFromUrlParam(paramName);
        if (!cfId) return;

        if (paramName.startsWith(FilterParamNames.cfIdAnd)) {
          customFields.push({
            id: cfId,
            filter: {
              and: values,
            },
          });
        } else if (paramName.startsWith(FilterParamNames.cfIdOr)) {
          customFields.push({
            id: cfId,
            filter: {
              or: values,
            },
          });
        } else if (paramName.startsWith(FilterParamNames.cfIdNot)) {
          customFields.push({
            id: cfId,
            filter: {
              not: values,
            },
          });
        }
      });
    }

    return customFields;
  }, [filters.customFields]);

  const createdDateParam = useMemo((): SearchFilterDate | null => {
    if (filters[FilterParamNames.recordedDateStart] || filters[FilterParamNames.recordedDateEnd]) {
      const createdDate: SearchFilterDate = {};

      if (filters[FilterParamNames.recordedDateStart]) {
        createdDate.start = filters[FilterParamNames.recordedDateStart];
      }

      if (filters[FilterParamNames.recordedDateEnd]) {
        createdDate.end = filters[FilterParamNames.recordedDateEnd];
      }

      return createdDate;
    }
    return null;
  }, [filters]);

  const updatedDateParam = useMemo((): SearchFilterDate | null => {
    if (filters[FilterParamNames.modifiedDateStart] || filters[FilterParamNames.modifiedDateEnd]) {
      const updatedDate: SearchFilterDate = {};

      if (filters[FilterParamNames.modifiedDateStart]) {
        updatedDate.start = filters[FilterParamNames.modifiedDateStart];
      }

      if (filters[FilterParamNames.modifiedDateEnd]) {
        updatedDate.end = filters[FilterParamNames.modifiedDateEnd];
      }

      return updatedDate;
    }
    return null;
  }, [filters]);

  const uploadedDateParam = useMemo((): SearchFilterDate | null => {
    if (filters[FilterParamNames.uploadedDateStart] || filters[FilterParamNames.uploadedDateEnd]) {
      const uploadedDate: SearchFilterDate = {};

      if (filters[FilterParamNames.uploadedDateStart]) {
        uploadedDate.start = filters[FilterParamNames.uploadedDateStart];
      }

      if (filters[FilterParamNames.uploadedDateEnd]) {
        uploadedDate.end = filters[FilterParamNames.uploadedDateEnd];
      }

      return uploadedDate;
    }
    return null;
  }, [filters]);

  const takenDateParam = useMemo((): SearchFilterDate | null => {
    if (filters[FilterParamNames.takenDateStart] || filters[FilterParamNames.takenDateEnd]) {
      const takenDate: SearchFilterDate = {};

      if (filters[FilterParamNames.takenDateStart]) {
        takenDate.start = filters[FilterParamNames.takenDateStart];
      }

      if (filters[FilterParamNames.takenDateEnd]) {
        takenDate.end = filters[FilterParamNames.takenDateEnd];
      }

      return takenDate;
    }
    return null;
  }, [filters]);

  const tagsParam = useMemo((): AssetSearchFilters['tag'] | null => {
    const tagIdAnd = filters[FilterParamNames.tagIdAnd];
    const tagIdOr = filters[FilterParamNames.tagIdOr];
    const tagIdNot = filters[FilterParamNames.tagIdNot];

    if (tagIdAnd?.length) {
      return { and: tagIdAnd };
    } else if (tagIdOr?.length) {
      return { or: tagIdOr };
    } else if (tagIdNot?.length) {
      return { not: tagIdNot };
    }
    return null;
  }, [filters]);

  const smartTagsParam = useMemo((): AssetSearchFilters['smartTag'] | null => {
    const smartTagIdAnd = filters[FilterParamNames.smartTagIdAnd];
    const smartTagIdOr = filters[FilterParamNames.smartTagIdOr];
    const smartTagIdNot = filters[FilterParamNames.smartTagIdNot];

    if (smartTagIdAnd?.length) {
      return { and: smartTagIdAnd };
    } else if (smartTagIdOr?.length) {
      return { or: smartTagIdOr };
    } else if (smartTagIdNot?.length) {
      return { not: smartTagIdNot };
    }
    return null;
  }, [filters]);

  const peopleParam = useMemo((): AssetSearchFilters['person'] | null => {
    const personIdAnd = filters[FilterParamNames.personIdAnd];
    const personIdOr = filters[FilterParamNames.personIdOr];

    if (personIdAnd?.length) {
      return { and: personIdAnd };
    } else if (personIdOr?.length) {
      return { or: personIdOr };
    }
  }, [filters]);

  const importedKeywordsParam = useMemo((): AssetSearchFilters['keyword'] | null => {
    const importedKeywordAnd = filters[FilterParamNames.importedKeywordAnd];
    const importedKeywordOr = filters[FilterParamNames.importedKeywordOr];
    const importedKeywordNot = filters[FilterParamNames.importedKeywordNot];

    if (importedKeywordAnd?.length) {
      return { and: importedKeywordAnd };
    } else if (importedKeywordOr?.length) {
      return { or: importedKeywordOr };
    } else if (importedKeywordNot?.length) {
      return { not: importedKeywordNot };
    }
    return null;
  }, [filters]);

  const sourcesParam = useMemo((): AssetSearchFilters['source'] | null => {
    const source = filters[FilterParamNames.source];
    if (source?.length) {
      return { or: source };
    }
    return null;
  }, [filters]);

  const camerasParam = useMemo((): AssetSearchFilters['camera'] | null => {
    const cameraModels = filters[FilterParamNames.cameraModel];
    const cameraMake = filters[FilterParamNames.cameraMake];
    if (cameraMake || !!cameraModels?.length) {
      if (cameraModels?.length) {
        // we allow selecting only one make and multiple models for that make
        return {
          or: cameraModels?.map((model) => ({
            make: filters[FilterParamNames.cameraMake] ?? undefined,
            model,
          })),
        };
      } else {
        // if there is no model, send only make
        return {
          or: [{ make: filters[FilterParamNames.cameraMake] }],
        };
      }
    }
    return null;
  }, [filters]);

  const uploadersParam = useMemo((): AssetSearchFilters['member'] | null => {
    const uploaderId = filters[FilterParamNames.uploaderId];
    if (uploaderId?.length) {
      return { or: uploaderId };
    }
    return null;
  }, [filters]);

  const copyrightsParam = useMemo((): AssetSearchFilters['copyright'] | null => {
    const copyright = filters[FilterParamNames.copyright];
    if (copyright?.length) {
      return { or: copyright };
    }
    return null;
  }, [filters]);

  const creatorsParam = useMemo((): AssetSearchFilters['creator'] | null => {
    const creator = filters[FilterParamNames.creator];
    if (creator?.length) {
      return { or: creator };
    }
    return null;
  }, [filters]);

  const colorsParam = useMemo((): AssetSearchFilters['color'] | null => {
    const color = filters[FilterParamNames.color];
    if (color?.length) {
      return {
        or: color,
        threshold: filters[FilterParamNames.colorThreshold],
      };
    }

    return null;
  }, [filters]);

  const bookmarkedParam = useMemo((): AssetSearchFilters['bookmarked'] | null => {
    if (accountId && filters[FilterParamNames.bookmarked]) {
      return { is: accountId };
    }
  }, [filters, accountId]);

  const isOnBoardsParam = useMemo((): AssetSearchFilters['isOnBoards'] | null => {
    if (!isNil(filters[FilterParamNames.isOnBoards])) {
      return { is: !!filters[FilterParamNames.isOnBoards] };
    }
  }, [filters]);

  const hasVersionsParam = useMemo((): AssetSearchFilters['hasMultipleVersions'] | null => {
    if (!isNil(filters[FilterParamNames.hasMultipleVersions])) {
      return { is: !!filters[FilterParamNames.hasMultipleVersions] };
    }
  }, [filters]);

  const untaggedParam = useMemo((): AssetSearchFilters['untagged'] | null => {
    if (!isNil(filters[FilterParamNames.untagged])) {
      return { is: !!filters[FilterParamNames.untagged] };
    }
  }, [filters]);

  const hasOpenCommentsParam = useMemo((): AssetSearchFilters['hasOpenComments'] | null => {
    if (!isNil(filters[FilterParamNames.hasOpenComments])) {
      return { is: !!filters[FilterParamNames.hasOpenComments] };
    }
  }, [filters]);

  const extensionsParam = useMemo((): AssetSearchFilters['extension'] | null => {
    const ext = filters[FilterParamNames.ext];
    if (ext?.length) {
      return { or: ext };
    }
    return null;
  }, [filters]);

  const locationParam = useMemo<SeparateLocationParam | null>(() => {
    const countriesFilter = filters[FilterParamNames.country];
    const statesFilter = filters[FilterParamNames.state];
    const citiesFilter = filters[FilterParamNames.city];

    if (!countriesFilter?.length && !statesFilter?.length && !citiesFilter?.length) return null;

    const locationParam: SeparateLocationParam = {};

    if (countriesFilter?.length) {
      locationParam.country = { or: countriesFilter };
    }

    if (statesFilter?.length) {
      locationParam.state = { or: statesFilter };
    }

    if (citiesFilter?.length) {
      locationParam.city = { or: citiesFilter };
    }

    return locationParam;
  }, [filters]);

  const locationsParam = useMemo(() => {
    const locationBoards = filters[FilterParamNames.locationBoards];
    const locationLibraries = filters[FilterParamNames.locationLibraries];

    if (!locationBoards?.length && !locationLibraries?.length) return null;

    const locationsParam: SeparateLocationsParam = {};

    if (locationBoards?.length) {
      locationsParam.boards = { or: locationBoards };
    }

    if (locationLibraries?.length) {
      locationsParam.libraries = { or: locationLibraries };
    }
    return locationsParam;
  }, [filters]);

  const videoFrameRatesParam = useMemo((): AssetSearchFilters['videoFrameRate'] | null => {
    const frameRates = filters[FilterParamNames.videoFrameRate];
    if (frameRates?.length) {
      return { or: frameRates.map((value) => Number(value)) };
    }
    return null;
  }, [filters]);

  const orientationsParam = useMemo((): AssetSearchFilters['orientation'] | null => {
    const orientations = filters[FilterParamNames.orientation];

    if (!Array.isArray(orientations) || !orientations.length) {
      return null;
    }

    if (orientations.length === 1) {
      return { is: orientations[0] };
    }

    return { or: orientations };
  }, [filters]);

  const videoAspectRatiosParam = useMemo((): AssetSearchFilters['videoAspectRatio'] | null => {
    const videoAspectRatio = filters[FilterParamNames.videoAspectRatio];
    if (videoAspectRatio?.length) {
      return { or: videoAspectRatio };
    }
    return null;
  }, [filters]);

  const audioCodingsParam = useMemo((): AssetSearchFilters['audioCoding'] | null => {
    const audioCoding = filters[FilterParamNames.audioCoding];
    if (audioCoding?.length) {
      return { or: audioCoding };
    }
    return null;
  }, [filters]);

  const audioSampleRatesParam = useMemo((): AssetSearchFilters['audioSampleRate'] | null => {
    const audioSampleRate = filters[FilterParamNames.audioSampleRate];
    if (audioSampleRate?.length) {
      return { or: audioSampleRate.map((value) => Number(value)) };
    }
    return null;
  }, [filters]);

  // Read similarToImage from Redux store, not from URL
  const semanticSimilarToImageParam = useMemo((): AssetSearchFilters['similarToImage'] | null => {
    if (similarToImageFromRedux) {
      return { is: similarToImageFromRedux };
    }
    return null;
  }, [similarToImageFromRedux]);

  const semanticClipSearchParam = useMemo((): AssetSearchFilters['similarTo'] | null => {
    if (filters[FilterParamNames.similarTo]) {
      return { is: filters[FilterParamNames.similarTo] };
    }
    return null;
  }, [filters]);

  const params = useDeepCompareMemo(
    () => ({
      audioSampleRatesParam,
      audioCodingsParam,
      customFieldsParam,
      updatedDateParam,
      createdDateParam,
      takenDateParam,
      bookmarkedParam,
      colorsParam,
      uploadersParam,
      boardParam,
      sourcesParam,
      tagsParam,
      uploadedDateParam,
      typesParam,
      assetFileKindParam,
      extensionsParam,
      isOnBoardsParam,
      hasVersionsParam,
      untaggedParam,
      hasOpenCommentsParam,
      libraryParam,
      onlyInLibrariesParam,
      importedKeywordsParam,
      copyrightsParam,
      creatorsParam,
      camerasParam,
      orientationsParam,
      videoFrameRatesParam,
      locationParam,
      smartTagsParam,
      videoAspectRatiosParam,
      peopleParam,
      locationsParam,
      semanticSimilarToImageParam,
      semanticClipSearchParam,
    }),
    [
      peopleParam,
      audioSampleRatesParam,
      audioCodingsParam,
      importedKeywordsParam,
      creatorsParam,
      camerasParam,
      orientationsParam,
      videoFrameRatesParam,
      locationParam,
      videoAspectRatiosParam,
      boardParam,
      bookmarkedParam,
      colorsParam,
      createdDateParam,
      customFieldsParam,
      takenDateParam,
      extensionsParam,
      hasOpenCommentsParam,
      hasVersionsParam,
      untaggedParam,
      isOnBoardsParam,
      sourcesParam,
      tagsParam,
      typesParam,
      assetFileKindParam,
      updatedDateParam,
      smartTagsParam,
      uploadedDateParam,
      uploadersParam,
      libraryParam,
      onlyInLibrariesParam,
      copyrightsParam,
      videoAspectRatiosParam,
      locationsParam,
      semanticSimilarToImageParam,
      semanticClipSearchParam,
    ],
  );

  return params;
};
